import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormContext } from 'react-hook-form';
import { isSameDay } from 'date-fns';
import { AlertTriangle, ChevronDown } from 'lucide-react';
import { Calendar } from '@components/ui/calendar';
import { Button } from '@components/ui/button';
import { FormField, FormMessage } from '@components/ui/form';
import { DrawerContent, DrawerRoot, DrawerTitle } from '@components/ui/drawer';
import { TimeWheel } from '@components/scheduling/timeWheel';
import { SchedulerCompanyTime } from '@components/scheduler/companyTime';
import { formatTime, numericDate } from '@utils/i18n';
import { getShift } from '@utils/schedule/tenantShift';
import { cn } from '@utils/cn';
import { getRelatedStartMinutes, getRelatedStarts, toMinutes, withTime } from './helpers';
import { ExternalScheduleWhenInputsProps } from './types';

const fieldClass =
  'flex h-11 w-full min-w-0 items-center gap-2 rounded-md border-[1.5px] border-neutral-300 bg-white pl-3 pr-2.5 text-left text-base disabled:bg-neutral-50 disabled:text-neutral-400';

export const ExternalScheduleWhenInputs = ({
  disabled,
  takenDays = [],
  currentDay,
  importantTasks,
}: ExternalScheduleWhenInputsProps) => {
  const { t } = useTranslation('app');
  const { control, watch, setValue, clearErrors, formState } = useFormContext<{
    date?: Date;
    time?: Date;
  }>();
  const [calendar, setCalendar] = useState<boolean | null>(null);
  const [timeOpen, setTimeOpen] = useState(false);
  const [draft, setDraft] = useState(0);
  const date = watch('date');
  const time = watch('time');
  const disabledDays = takenDays.filter((day) => !currentDay || !isSameDay(day, currentDay));
  const relatedStarts = getRelatedStarts(importantTasks);
  const calendarOpen = !disabled && (calendar ?? !date);
  const noRelatedWork =
    Boolean(date) &&
    relatedStarts.length > 0 &&
    !relatedStarts.some((start) => date && isSameDay(start, date));

  const pickDay = (day: Date) => {
    setCalendar(false);
    setValue('date', day, { shouldDirty: true });
    clearErrors('date');
    if (time) return;
    const relatedStart = getRelatedStartMinutes(day, relatedStarts);
    if (relatedStart === undefined) return;
    setValue('time', withTime(day, relatedStart), { shouldDirty: true });
    clearErrors('time');
  };

  const openTime = () => {
    setCalendar(false);
    setDraft(
      toMinutes(time) ?? (date && getRelatedStartMinutes(date, relatedStarts)) ?? getShift().start,
    );
    setTimeOpen(true);
  };

  const applyTime = () => {
    setValue('time', withTime(time || date || new Date(), draft), { shouldDirty: true });
    clearErrors('time');
    setTimeOpen(false);
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={control}
          name="date"
          render={() => (
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-sm text-neutral-600">{t('forms.date.label')}*</span>
              <button
                type="button"
                disabled={disabled}
                aria-expanded={calendarOpen}
                onClick={() => setCalendar(!calendarOpen)}
                className={cn(
                  fieldClass,
                  formState.errors.date && 'border-danger-500',
                  calendarOpen && 'border-2 border-primary-500',
                )}>
                <span className={cn('min-w-0 flex-1 truncate', !date && 'text-neutral-400')}>
                  {date ? numericDate(date) : t('forms.default.select.placeholder')}
                </span>
              </button>
              <FormMessage />
            </div>
          )}
        />
        <FormField
          control={control}
          name="time"
          render={() => (
            <div className="flex min-w-0 flex-col gap-1">
              <span className="text-sm text-neutral-600">{t('forms.time.label')}*</span>
              <button
                type="button"
                disabled={disabled}
                aria-haspopup="dialog"
                aria-expanded={timeOpen}
                onClick={openTime}
                className={cn(
                  fieldClass,
                  formState.errors.time && 'border-danger-500',
                  timeOpen && 'border-2 border-primary-500',
                )}>
                <span
                  className={cn(
                    'min-w-0 flex-1 truncate tabular-nums',
                    time ? 'font-semibold text-neutral-800' : 'text-neutral-400',
                  )}>
                  {time ? formatTime(time) : t('forms.default.select.placeholder')}
                </span>
                <ChevronDown className="h-4 w-4 shrink-0 text-neutral-500" />
              </button>
              <FormMessage />
            </div>
          )}
        />
      </div>
      {calendarOpen && (
        <div className="flex justify-center rounded-xl border border-neutral-200 bg-white [&>div]:max-h-none">
          <Calendar
            mode="single"
            selected={date}
            defaultMonth={date}
            weekStartsOn={1}
            disabled={disabledDays}
            onSelect={(value) => value && pickDay(value)}
            classNames={{
              weekday: 'w-10 text-[0.8rem] font-normal text-neutral-500',
              day_button: 'h-10 w-10 rounded-full p-0 hover:bg-primary-50',
              selected: 'rounded-full bg-primary-500 text-white',
            }}
          />
        </div>
      )}
      {noRelatedWork && (
        <div className="flex items-center gap-2 rounded-lg border border-warning-200 bg-warning-50 px-3 py-2 text-sm font-semibold text-warning-700">
          <AlertTriangle className="h-4 w-4 shrink-0 text-warning-500" />
          {t('scheduling.external.noRelatedWork')}
        </div>
      )}
      <SchedulerCompanyTime />
      <DrawerRoot
        open={timeOpen}
        direction="bottom"
        onOpenChange={(value) => {
          if (!value) setTimeOpen(false);
        }}>
        <DrawerContent
          aria-describedby={undefined}
          overlayClassName="z-[70] bg-[rgba(24,28,33,.45)]"
          className="z-[70] gap-3 rounded-t-2xl border-0 px-4 pb-[calc(16px+env(safe-area-inset-bottom,0px))] pt-2 shadow-[0_-8px_30px_rgba(0,0,0,.18)]">
          <div className="mx-auto h-1.5 w-10 shrink-0 rounded-full bg-neutral-200" />
          <DrawerTitle className="text-center text-[17px] font-bold text-neutral-800">
            {t('forms.time.label')}
          </DrawerTitle>
          {timeOpen && (
            <TimeWheel label={t('forms.time.label')} value={draft} onChange={setDraft} />
          )}
          <div className="grid grid-cols-2 gap-2">
            <Button
              variant="secondary"
              className="h-11 justify-center"
              onClick={() => setTimeOpen(false)}>
              {t('actions.cancel')}
            </Button>
            <Button className="h-11 justify-center" onClick={applyTime}>
              {t('actions.done')}
            </Button>
          </div>
        </DrawerContent>
      </DrawerRoot>
    </div>
  );
};
